import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Skeleton,
  Stack,
  Switch,
  Typography,
  useMediaQuery
} from '@mui/material';

// project import
import Transitions from 'components/@extended/Transitions';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import Avatar from 'components/@extended/Avatar';
import Chip from 'components/ui/Chip';
import { useBillingClient } from 'client/client-hook';
import { BillingOverviewReply, ListSubscriptionsReply } from 'proto/billing-manager_pb';
import { PLAN_BADGE_LIST_SUBSCRIPTION_STATUSES, resolvePlanBadge } from 'lib/billing';
import { useSubscriptionFreeze } from 'features/subscription-freeze/SubscriptionFreezeContext';
import { FREEZE_NAME } from 'features/subscription-freeze/content';

// assets
import { Crisp } from 'crisp-sdk-web';
import {
  BulbOutlined,
  CompassOutlined,
  CustomerServiceOutlined,
  DownOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useTour } from 'contexts/TourContext';
import { ROUTES_TOUR } from 'sections/welcome-tour/tourRoutes';
import { sizeByBreakpoint, textSizeByBreakpoint } from '../../../../../utils/helper';
import { CustomListItemProps } from '../../../../../types/layout';

// types
// interface TabPanelProps {
//   children?: ReactNode;
//   dir?: string;
//   index: number;
//   value: number;
// }

// tab panel wrapper
// function TabPanel(props: TabPanelProps) {
//   const { children, value, index, ...other } = props;

//   return (
//     <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
//       {value === index && children}
//     </div>
//   );
// }

// function a11yProps(index: number) {
//   return {
//     id: `profile-tab-${index}`,
//     'aria-controls': `profile-tabpanel-${index}`
//   };
// }

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { mode, onChangeMode } = useConfig();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { logout, user } = useAuth();
  const { start: startTour } = useTour();
  const { freeze } = useSubscriptionFreeze();
  const getBillingClient = useBillingClient();
  const [billingOverview, setBillingOverview] = useState<BillingOverviewReply.AsObject | null>(null);

  useEffect(() => {
    if (user?.tcId) {
      getBillingClient().then(async (client) => {
        try {
          const [overviewRes, subscriptionsRes] = await Promise.all([
            client.billingOverview({ userId: user.tcId || '' }),
            client.listSubscriptions({ userId: user.tcId || '', statusList: [...PLAN_BADGE_LIST_SUBSCRIPTION_STATUSES] })
          ]);

          const overview = overviewRes as BillingOverviewReply.AsObject;
          const subscriptionList = (subscriptionsRes as ListSubscriptionsReply.AsObject).subscriptionsList || [];

          // Plan badge is 3-tier: "Premium" (premium-plan role + a copier plan),
          // "Standard" (a copier plan without the role), or "Free" (no copier).
          // resolvePlanBadge is line-item aware, so bundle subs and add-ons
          // (Analyzer/Dedicated) are never misread as the plan tier.
          setBillingOverview({
            ...overview,
            activeSubscription: resolvePlanBadge(subscriptionList, user?.roles)
          });
        } catch (error) {
          console.error('Failed to fetch billing overview:', error);
        }
      });
    }
  }, [user, getBillingClient]);

  const handleLogout = async () => {
    try {
      Crisp.session.reset();
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleModeChange = () => {
    const newMode = mode === 'dark' ? 'light' : 'dark';
    onChangeMode(newMode);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  // const [value, setValue] = useState(0);

  // const handleChange = (event: SyntheticEvent, newValue: number) => {
  //   setValue(newValue);
  // };

  const CustomListItem: React.FC<CustomListItemProps> = ({
    Icon,
    primaryText,
    secondaryText,
    children,
    onClick,
    sx,
    hoverEffect = true
  }) => {
    return (
      <ListItem
        component="div"
        sx={{
          cursor: hoverEffect ? 'pointer' : 'normal',
          padding: 0,
          '&:hover': {
            backgroundColor: 'transparent',
            ...(hoverEffect
              ? {
                  '& .MuiListItemIcon-root': {
                    color: theme.palette.primary.main,
                    transition: 'color 100ms ease-in-out'
                  },
                  '& .MuiListItemText-root': {
                    color: theme.palette.primary.main,
                    transition: 'color 100ms ease-in-out'
                  }
                }
              : {})
          },
          ...sx
        }}
        onClick={onClick}
      >
        <ListItemIcon sx={{ minWidth: sizeByBreakpoint(1.5, 32) }}>
          <Box
            component={Icon}
            sx={{
              fontSize: textSizeByBreakpoint(18)
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={primaryText}
          secondary={secondaryText}
          primaryTypographyProps={{
            fontSize: textSizeByBreakpoint(14),
            fontWeight: 500
          }}
          sx={{
            margin: 0
          }}
        />
        {children}
      </ListItem>
    );
  };

  const initials =
    (user?.name || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p: string) => p[0]?.toUpperCase())
      .join('') || 'U';

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={{
          px: isMobile ? 0 : 0.75,
          py: isMobile ? 0 : 0.5,
          bgcolor: open ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
          border: `1px solid ${open ? alpha(theme.palette.primary.main, 0.2) : 'transparent'}`,
          borderRadius: '10px',
          cursor: 'pointer',
          justifyContent: 'flex-start',
          transition: 'background-color 150ms ease, border-color 150ms ease',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderColor: alpha(theme.palette.primary.main, 0.15)
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.secondary.dark}`,
            outlineOffset: 2
          }
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        {isMobile ? (
          <Avatar alt={user?.name || 'profile user'} size="sm" color="warning">
            {initials}
          </Avatar>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar alt={user?.name || 'profile user'} size="sm" color="warning">
              {initials}
            </Avatar>
            <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
              <Typography variant="subtitle1" sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }}>
                {user?.name}
              </Typography>
              {freeze || billingOverview ? (
                <Chip
                  label={freeze ? FREEZE_NAME : billingOverview?.activeSubscription || 'Free'}
                  size="small"
                  fontWeight={600}
                  customColor={mode === 'dark' ? alpha(theme.palette.primary.main, 0.18) : alpha(theme.palette.primary.main, 0.12)}
                  customTextColor={mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark}
                  sx={{ cursor: 'pointer', fontSize: '0.65rem', height: '16px', borderRadius: '4px', px: 0.5 }}
                />
              ) : (
                <Skeleton variant="rounded" width={48} height={16} />
              )}
            </Box>
            <Box
              sx={{
                ml: 0.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                borderRadius: '6px',
                color: open ? theme.palette.primary.main : 'text.secondary',
                bgcolor: open ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                fontSize: '0.5rem',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s ease, background-color 0.2s ease'
              }}
            >
              <DownOutlined />
            </Box>
          </Stack>
        )}
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            {open && (
              <Paper
                sx={{
                  boxShadow: theme.customShadows.headerDropdown,
                  marginTop: 0.5,
                  width: 280,
                  minWidth: 280,
                  maxWidth: 280,
                  maxHeight: 'calc(100vh - 100px)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  [theme.breakpoints.down('md')]: {
                    width: 260,
                    minWidth: 260,
                    maxWidth: 260
                  }
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: 'auto',
                    padding: sizeByBreakpoint(0.75, 14),
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}
                >
                  <ClickAwayListener onClickAway={handleClose}>
                    <Stack direction="column" gap={sizeByBreakpoint(0.75, 14)} divider={<Divider />}>
                      <Stack direction="column" gap={sizeByBreakpoint(0.75, 10)}>
                        {/* Header Section */}
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar alt={user?.name || 'profile user'} size="md" color="warning">
                            {initials}
                          </Avatar>
                          <Box
                            sx={{
                              textAlign: 'left',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.5,
                              alignItems: 'flex-start',
                              minWidth: 0,
                              flex: 1
                            }}
                          >
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%' }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  lineHeight: 1.2,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  minWidth: 0
                                }}
                              >
                                {user?.name}
                              </Typography>
                              {(freeze || billingOverview) && (
                                <Chip
                                  label={freeze ? FREEZE_NAME : billingOverview?.activeSubscription || 'Free'}
                                  size="small"
                                  fontWeight={600}
                                  customColor={
                                    mode === 'dark' ? alpha(theme.palette.primary.main, 0.18) : alpha(theme.palette.primary.main, 0.12)
                                  }
                                  customTextColor={mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark}
                                  sx={{ fontSize: '0.65rem', height: '16px', borderRadius: '4px', px: 0.5, flexShrink: 0 }}
                                />
                              )}
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '100%'
                              }}
                            >
                              {user?.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>

                      {/* Dark Mode */}
                      <CustomListItem Icon={BulbOutlined} primaryText="Dark Mode" hoverEffect={false}>
                        <Switch size="small" color="primary" checked={mode === 'dark'} onChange={handleModeChange} sx={{ margin: 0 }} />
                      </CustomListItem>

                      <Stack direction="column" gap={sizeByBreakpoint(0.75, 14)}>
                        {/* Profile & Settings */}
                        <CustomListItem
                          Icon={UserOutlined}
                          primaryText="Profile & Settings"
                          onClick={() => handleNavigate('/account-settings')}
                        />
                        {/* Subscription & Billing */}
                        <CustomListItem
                          Icon={SettingOutlined}
                          primaryText="Subscription & Billing"
                          onClick={() => handleNavigate('/account-settings/billing')}
                        />
                      </Stack>

                      <Stack direction="column" gap={sizeByBreakpoint(0.75, 14)}>
                        {/* Welcome tour — replays from the top, rather than
                            resuming wherever it was abandoned. */}
                        <CustomListItem
                          Icon={CompassOutlined}
                          primaryText="Welcome tour"
                          onClick={() => {
                            startTour();
                            handleNavigate(ROUTES_TOUR.TOUR);
                          }}
                        />
                        {/* Guides */}
                        <CustomListItem
                          Icon={QuestionCircleOutlined}
                          primaryText="Guides"
                          onClick={() => window.open('https://docs.tradersconnect.com', '_blank')}
                        />
                        {/* Live Chat */}
                        <CustomListItem
                          Icon={CustomerServiceOutlined}
                          primaryText="Live Chat"
                          onClick={() => {
                            setOpen(false);
                            Crisp.chat.show();
                            Crisp.chat.open();
                          }}
                        />
                      </Stack>

                      <Stack direction="column" gap={sizeByBreakpoint(0.75, 14)}>
                        {/* Log out */}
                        <CustomListItem
                          Icon={LogoutOutlined}
                          primaryText="Log out"
                          onClick={handleLogout}
                          hoverEffect={false}
                          sx={{
                            '& .MuiListItemIcon-root': {
                              color: theme.palette.error.main
                            },
                            '& .MuiListItemText-root': {
                              color: theme.palette.error.main
                            },
                            cursor: 'pointer'
                          }}
                        />
                      </Stack>
                    </Stack>
                  </ClickAwayListener>
                </Box>
              </Paper>
            )}
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Profile;
